/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useMemo, useState } from 'react';
import { Button, Input, InputGroup } from 'reactstrap';
import { DashboardRow } from '../../dashboard';
import { BlockingLoader } from '../../loader/BlockingLoader';
import { QuestionInput } from '../../question/QuestionInput';
import { getTypeName, UtrvStatus, UtrvType } from '../../../constants/status';
import {
  getCurrentStartOfTheDayDate,
  getDate,
  getFullUTCDate,
  getMonth,
  getUTCEndOf,
  getYear,
} from '../../../utils/date';
import { InitialResettableQuestionState, ValueData } from '../../survey/question/questionInterfaces';
import UniversalTrackerValue, { NewUniversalTrackerValuePlain } from '../../../model/UniversalTrackerValue';
import { unload } from '../../../slice/utrvChartDataSlice';
import { useDispatch } from 'react-redux';
import { addSiteAlert } from '../../../slice/siteAlertsSlice';
import { createUtrvByModalService } from '../../../actions/universalTrackerModal';
import { UniversalTrackerPlain, UtrValueType, TableColumnType } from '../../../types/universalTracker';
import { ColumnInputProps } from '../../survey/form/input/table/InputInterface';
import { BaseInputProps } from '../../survey/form/input/InputProps';
import { canAddTarget } from '../../../utils/universalTracker';
import UniversalTracker from '../../../model/UniversalTracker';

export interface DataFormState {
  comments?: undefined;
  year?: undefined;
  saved: boolean;
  type: UtrvType;
  message?: string;
  saving: boolean;
  month?: undefined;
  action?: string;
  files: any[];
  value?: string | number | boolean;
  valueData?: ValueData;
  sampleSize?: number | boolean;
  effectiveDate?: string;
  errored: boolean
}

const currentMonth = getMonth();
const currentYear = getYear();
const startYear = currentYear - 5;
const getYearOptions = (maxYear: number) => {
  const years = [];
  for (let year = maxYear; year > startYear; year--) {
    years.push(year);
  }
  return years;
}

const getMonthOptions = () => [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface SetDataFormProps {
  utrvType: UtrvType;
  handleBack?: () => void;
  utr: UniversalTracker;
  initiativeId?: string;
  userId?: string;
  reloadUtrvs: () => void;
}

function getEffectiveDate(selectedMonth: number, selectedYear: number) {
  let effectiveDate = new Date();
  effectiveDate = getFullUTCDate(selectedYear, selectedMonth - 1, 1, effectiveDate);
  effectiveDate = getUTCEndOf('month', effectiveDate);
  return effectiveDate.toISOString();
}
export const SetDataForm = (props: SetDataFormProps) => {
  const { utr, handleBack, utrvType, initiativeId, userId, reloadUtrvs } = props;

  const dispatch = useDispatch();

  const [selectedMonth, setSelectedMonth] = useState(currentMonth + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [formData, setFormData] = useState<InitialResettableQuestionState>();
  const [isSaving, setSaving] = useState(false);

  const maxYear = utrvType === UtrvType.Target ? currentYear + 10 : currentYear;
  const newUtrv = useMemo(() => {
    if (!initiativeId || !userId) {
      return;
    }

    return new UniversalTrackerValue({
      status: UtrvStatus.Created,
      effectiveDate: getCurrentStartOfTheDayDate('day').toISOString(),
      sourceType: 'manual',
      type: UtrvType.Target,
      history: [],
      stakeholders: {
        stakeholder: [userId],
        verifier: [],
      },
      evidenceRequired: false,
      verificationRequired: false,
      lastUpdated: getDate().toISOString(),
      initiativeId,
      universalTrackerId: utr.getId(),
      _new: true
    });
  }, [initiativeId, userId, utr]);

  if (!newUtrv) {
    return <BlockingLoader />;
  }

  const handleSubmit = () => {
    if (!formData || isSaving) {
      return false;
    }

    setSaving(true);
    const universalTrackerValue = newUtrv.getUniversalTrackerValue();

    const newData: DataFormState = {
      ...formData,
      comments: undefined,
      errored: false,
      saved: false,
      type: utrvType,
      effectiveDate: getEffectiveDate(selectedMonth, selectedYear)
    }

    dispatch(
      createUtrvByModalService(newData, universalTrackerValue as NewUniversalTrackerValuePlain))
      .then(async () => {
        await dispatch(unload());
        await reloadUtrvs();
        handleBack?.();
      })
      .catch((e) => {
        dispatch(addSiteAlert({
          content: e.message,
        }))
      })
      .finally(() => setSaving(false));
  }

  const isDisabled = (props?: Partial<BaseInputProps> | Partial<ColumnInputProps>): boolean => {
    if (!props?.universalTracker) {
      return true;
    }

    const utrPlain: UniversalTrackerPlain = props.universalTracker.getRaw();
    if (utrPlain.valueType === UtrValueType.Table) {
      if ('column' in props) {
        const validColumnTypes = [TableColumnType.Number, TableColumnType.Percentage];
        if (props.column?.type && validColumnTypes.includes(props?.column?.type)) {
          return false;
        }
      }
      return true;
    }

    return !canAddTarget(utrPlain);
  }

  return (
    <>
      <hr className='mx-3' />
      <DashboardRow title={`Set the ${getTypeName(utrvType)} date`} headingStyle={5} >
        <div className='p-2'>
          <InputGroup>
            <Input
              name='month'
              type='select'
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              defaultValue={selectedMonth}
              style={{ minWidth: '130px' }}>
              {getMonthOptions().map((month, i) => <option key={i} value={i + 1}>{month}</option>)}
            </Input>
            <Input
              name='year'
              type='select'
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              defaultValue={selectedYear}>
              {getYearOptions(maxYear).map((year) => <option key={year} value={year}>{year}</option>)}
            </Input>
          </InputGroup>
        </div>
      </DashboardRow>

      <DashboardRow title={`Set the ${getTypeName(utrvType)}`} headingStyle={5}>
        <div className='w-100 p-2'>
          <QuestionInput
            utrv={newUtrv.universalTrackerValue}
            utr={utr}
            handleUpdate={setFormData}
            isDisabled={isDisabled}
          />
        </div>
      </DashboardRow>

      <DashboardRow>
        <div className='flex-fill text-right'>
          {handleBack ? <Button color='link-secondary' onClick={handleBack} className='mr-3'>Cancel</Button> : null}
          <Button color='primary' onClick={handleSubmit}>Save</Button>
        </div>
      </DashboardRow>
    </>
  );
}
